import { useContext } from 'react';
import { closestCenter, DndContext, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { QuestionBox } from '../QuestionBox';
import { LangContext } from '@/context/LangProvider';
import { InterviewQuestionsChildren } from '@/types';
import { isCheckpoint, isQuestion } from '@/utils/questionGroupHelpers';
import { CheckpointRow } from './CheckpointRow';
import {
  DiscussionGuideFormProps,
  DiscussionGuideProjectProps,
  DiscussionGuideQuestionProps,
} from './types';

interface FlatModeViewProps {
  // Core structure
  structure: {
    isRootRandomized: boolean;
    hasQuestions: boolean;
    isDisabled: boolean;
    isUserViewer: boolean;
  };
  // Drag & drop
  dragAndDrop: {
    onDragEnd: (event: DragEndEvent) => void;
    onDragStart: (event: DragStartEvent) => void;
  };
  // Handlers
  handlers: {
    onDeleteQuestion: (idToDelete: string) => void;
    isQuestionInDatabase: (questionId: string) => boolean;
    // TELV1-421 — embed-only; undefined for interview projects.
    onCheckpointLabelChange?: (checkpointId: string, label: string) => void;
    onDeleteCheckpoint?: (checkpointId: string) => void;
  };
  // TELV1-421 — raw root children (questions + checkpoints) in authoring order.
  // Drives rendering so a checkpoint in a flat embed stays visible / editable /
  // reorderable. For interview projects this is questions-only, so the
  // checkpoint branch is never hit and the render is identical to before.
  rawChildren: InterviewQuestionsChildren;
  // Form props
  formProps: DiscussionGuideFormProps;
  // Project props
  projectProps: DiscussionGuideProjectProps;
  // Question props
  questionProps: DiscussionGuideQuestionProps;
}

export function FlatModeView({
  structure,
  dragAndDrop,
  handlers,
  rawChildren,
  formProps,
  projectProps,
  questionProps,
}: FlatModeViewProps) {
  const { isRootRandomized, hasQuestions, isDisabled, isUserViewer } = structure;
  const { onDragEnd, onDragStart } = dragAndDrop;
  const { onDeleteQuestion, isQuestionInDatabase, onCheckpointLabelChange, onDeleteCheckpoint } =
    handlers;
  const { register, control, setValue, clearErrors, errors } = formProps;
  const { isProjectPublished, isProjectCompleted, singleProjectData } = projectProps;
  const { questions, openQuestionId, setOpenQuestionId, setFileData, fileData, currentQuestions } =
    questionProps;
  const { lang } = useContext(LangContext);

  // TELV1-421 — drag registration runs over the raw child ids (questions AND
  // checkpoints) so both reorder. GroupModeView uses the same id-string shape.
  const sortableItems = rawChildren.map((child) => ('id' in child ? child.id : '')).filter(Boolean);

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd} onDragStart={onDragStart}>
      <div
        className={`flex flex-col w-full h-full gap-5 p-6 rounded-md border ${isRootRandomized && hasQuestions ? 'bg-green-50 border-green-500' : 'bg-slate-50 border-transparent'}`}
      >
        {!hasQuestions && (
          <p className="text-sm font-medium">{lang.get('msg.questionsFieldEmpty')}</p>
        )}
        <SortableContext
          disabled={isDisabled}
          items={sortableItems}
          strategy={verticalListSortingStrategy}
        >
          {rawChildren.map((child, rawIndex) => {
            // The validation schema validates against the raw root children, so
            // errors are keyed by the raw child index (not the flattened,
            // checkpoint-stripped question index).
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const interviewQuestionsError = errors?.interview_questions as any;

            // TELV1-421 — render a checkpoint inline (embed-only). Interview
            // trees never contain checkpoints, so this branch is inert there.
            if (isCheckpoint(child)) {
              return (
                <CheckpointRow
                  key={child.id}
                  checkpoint={child}
                  isDisabled={isDisabled}
                  onLabelChange={onCheckpointLabelChange ?? (() => {})}
                  onDelete={onDeleteCheckpoint ?? (() => {})}
                  error={interviewQuestionsError?.children?.[rawIndex]?.label}
                  clearError={() => clearErrors(`interview_questions.children[${rawIndex}].label`)}
                />
              );
            }

            if (!isQuestion(child)) {
              // Groups never appear in flat mode (shouldRenderGroupUI gates that);
              // guard defensively rather than mis-render.
              return null;
            }

            const questionError = interviewQuestionsError?.children?.[rawIndex]?.question;
            const registrationPath = `interview_questions.children[${rawIndex}]`;
            // Question number ignores checkpoints (they do not consume a number).
            const displayNumber = rawChildren.slice(0, rawIndex + 1).filter(isQuestion).length;
            // currentQuestions is still the flattened (checkpoint-stripped) list,
            // so match by id rather than by the raw index.
            const currentQuestion = currentQuestions?.find((q) => q.id === child.id);

            return (
              <QuestionBox
                isProjectPublished={isProjectPublished}
                isProjectCompleted={isProjectCompleted}
                isActiveDrag={false}
                key={child.id}
                treePath={[rawIndex]}
                registrationPath={registrationPath}
                displayNumber={displayNumber}
                register={register}
                id={child.id}
                control={control}
                handleDeleteQuestion={onDeleteQuestion}
                isUserViewer={isUserViewer}
                existingText={child.question}
                singleProjectData={singleProjectData}
                questions={questions}
                openQuestionId={openQuestionId}
                setOpenQuestionId={setOpenQuestionId}
                setFileData={setFileData}
                fileData={fileData}
                currentQuestion={currentQuestion}
                setValue={setValue}
                clearErrors={clearErrors}
                error={questionError}
                isQuestionSaved={isQuestionInDatabase(child.id)}
              />
            );
          })}
        </SortableContext>
      </div>
    </DndContext>
  );
}
